<template>
  <div
    :ref="'completion__' + widgetIndex"
    class="completion__container animated fadeIn"
    :class="'completion_' + widgetIndex">
    <p v-show="!loaded" class="no__data loading">Loading...</p>
    <p v-if="loaded && emptyData" class="no__data">No Data Available</p>
    <div v-click-outside="hidePopover" class="completion__list row m-0">
      <div v-for="item in filteredSummaries"
        :ref="'shipment__' + item.id"
        :class="'shipment__' + item.id"
        :key="item.id"></div>
    </div>
  </div>
</template>
<script>
import { select, event } from 'd3';
import { isEmpty, first, get, cloneDeep } from 'lodash';
import { format } from 'date-fns';
import ClickOutside from 'vue-click-outside';
import chart from './index';
import dummy_data from './sample_data.json';

export default {
  name: 'widget_completion',
  data() {
    return {
      dummy_data,
      summaries: [],
      filteredSummaries: [],
      loaded: false,
      popover: null,
      tooltip: null,
      timeout: null,
      filterState: ['ON_PROCESS', 'CREATED', 'FINISH', 'FINISH_LOADING'],
      historyProgress: {},
      useTestFakeProgress: false,
      reloadDelayTime: 15e3,
    };
  },
  props: {
    widgetIndex: {
      type: String,
      required: true,
    },
  },
  beforeDestroy() {
    clearTimeout(this.timeout);
  },
  created() {
    this.loadData().then(() => {
      this.setupHeight();
      this.onResizeWindow();
      this.loaded = true;
    });
  },
  computed: {
    shipments() {
      return this.$store.getters['shipment_md/shipments/shipments'];
    },
    emptyData() {
      return isEmpty(this.summaries);
    },
    minimizeSidebar() {
      return this.$store.getters.minimizeSidebar;
    },
  },
  methods: {
    loadData() {
      this.$store.commit('shipment_md/shipments/SET_STATE_FILTER', this.filterState);
      const process = (shipments) => {
        if (!isEmpty(shipments)) {
          this.summaries = shipments;
          this.filteredSummaries = this.filterSummary(shipments);

          setTimeout(() => {
            this.drawChart();
            this.reloadData();
          }, 1e3);
        }
      };

      return this.$store.dispatch('shipment_md/shipments/LOAD_SUMMARY').then(process);
    },
    reloadData() {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.loadData();
      }, this.reloadDelayTime);
    },
    filterSummary(shipments) {
      const filteredData = [];

      if (!isEmpty(shipments)) {
        shipments.forEach((shipment) => {
          const isOnProcess = shipment.total_percentage < 100;
          const updateToday = format(new Date(), 'YYYY-MM-DD') === format(shipment.updated_at, 'YYYY-MM-DD');
          const todayFinish = updateToday && shipment.total_percentage === 100;

          if (isOnProcess || todayFinish) {
            if (this.useTestFakeProgress) {
              const reformatDistance = this.setupZeroDistance(shipment);
              const updatedProgress = this.updateFakeProgress(reformatDistance);
              filteredData.push(updatedProgress);
            } else {
              const reformatDistance = this.setupZeroDistance(shipment);
              filteredData.push(reformatDistance);
            }
          }
        });
      }

      return filteredData;
    },
    setupZeroDistance(shipment) {
      const { destinations } = shipment;
      const newDestinations = destinations.map((destination) => {
        const { direction } = destination;
        if (isEmpty(direction)) {
          destination.direction = 1e3;
        }
        return destination;
      });

      shipment.destinations = newDestinations;
      return shipment;
    },
    get(object, key) {
      return get(object, key) || '';
    },
    createPopup(popupType, dt) {
      let popup = null;
      const wgIndex = this.widgetIndex;

      if (popupType === 'popover') {
        select(`.popover__${this.widgetIndex}`).remove();

        const popover = select('body').selectAll(`.popover__${wgIndex}`).data([dt]);
        popup = popover
          .enter()
          .append('div')
          .classed(`popover__${wgIndex}`, true)
          .style('opacity', 0)
          .style('background-color', '#fff')
          .style('border', '1px solid #0000001a')
          .style('border-radius', '5px')
          .style('padding', '15px')
          .style('width', '300px')
          .style('z-index', 9999)
          .style('position', 'absolute')
          .style('pointer-events', 'none');
      }

      if (popupType === 'tooltip') {
        select(`.tooltip__${this.widgetIndex}`).remove();

        const tooltip = select('body').selectAll(`.tooltip__${wgIndex}`).data([dt]);
        popup = tooltip
          .enter()
          .append('div')
          .classed(`tooltip__${wgIndex}`, true)
          .style('padding', '5px')
          .style('background-color', '#fff')
          .style('border', '1px solid #0000001a')
          .style('border-radius', '5px')
          .style('opacity', 0)
          .style('z-index', 9999)
          .style('position', 'absolute')
          .style('pointer-events', 'none');
      }

      return popup;
    },
    drawChart() {
      let i = 0;
      const { filteredSummaries: shipments } = this;
      const mouseEvents = {
        onDropClick: this.onDropClick,
        onDropMouseover: this.onDropMouseover,
        onDropMouseout: this.onDropMouseout,
        onLinkClick: this.onLinkClick,
        hidePopover: this.hidePopover,
      };
      const wgDimension = this.getContainerDimension();
      const draw = () => {
        const shipment = shipments[i];
        if (shipment) {
          const shipmentId = `shipment__${shipment.id}`;
          const shipmentDiv = this.$refs[shipmentId];

          select(first(shipmentDiv))
            .data([shipment])
            .call(chart(mouseEvents, wgDimension));

          i += 1;
          draw();
        }
      };
      draw();
    },
    onDropClick(dropType) {
      return (dt) => {
        const { orders, name } = dt;
        let content = '';

        this.onDropMouseout(2e2)();
        this.popover = this.createPopup('popover', dt);
        this.popover
          .transition()
          .duration(2e2)
          .style('opacity', 1)
          .style('pointer-events', 'auto');

        const setElem = (returnType) => {
          let tabs = '<ul class="nav nav-tabs" role="tablist">';
          let tabContents = '<div class="tab-content" style="margin-top: 10px; max-height: 300px; overflow-y: auto;">';
          const hrStyles = 'margin-top: 5px; margin-bottom: 5px;';
          const ulStyles = `
            margin-bottom: 0px;
            list-style-type: none;
            padding-left: 5px;
            padding-right: 5px;
          `;
          const liStyles = `
            margin-bottom: 4px;
            border-bottom: 1px solid #0000001a;
          `;

          orders.forEach((order, i) => {
            const {
              id, units, number, status,
            } = order;
            const isActive = i === 0;
            const classActive = isActive ? 'active' : '';
            const classShow = isActive ? 'show' : '';
            const numb = i + 1;

            if (returnType === 'tab') {
              tabs += `<li class="nav-item"><a" class="nav-link ${classActive}" id="${id}-tab" data-toggle="tab" href="#${id}" role="tab" aria-controls="${id}" aria-selected="${isActive}">${numb}</a></li>`;
            } else {
              let orderContent = '';

              orderContent += `<p style="margin-bottom: 0px;">Order Number : <strong>${number}</strong></p><hr style="${hrStyles}">`;
              orderContent += `<p style="margin-bottom: 0px;">Status : <strong>${status}</strong></p><hr style="${hrStyles}">`;
              orderContent += `<p class="text-center" style="margin-bottom: 0px;"><strong>Units</strong></p><hr style="${hrStyles}">`;
              orderContent += `<ul style="${ulStyles}" class="unit-list">`;
              units.forEach((unit) => {
                const { number: unitNumber, type } = unit;
                orderContent += `<li style="${liStyles}">${unitNumber} - ${type}</li>`;
              });
              orderContent += '</ul>';
              tabContents += `<div class="order-detail tab-pane fade ${classShow} ${classActive}" id="tab-${id}" role="tabpanel" aria-labelledby="${id}-tab">${orderContent}</div>`;
            }
          });

          return returnType === 'tab' ? `${tabs} </ul>` : `${tabContents} </div>`;
        };

        if (dropType === 'destination') {
          content = `
            <p style="margin-bottom: 0px" class="text-center">${name}</p>
            <hr style="margin-top: 5px;">
            ${setElem('tab')}
            ${setElem('tab-content')}
          `;
        }

        if (dropType === 'current') {
          content = `
            <div style="padding: 5px;" class="latest-position">
            <p style="margin-bottom: 0px">Address : <strong>${this.get(dt, 'vehicle.gps_devices.0.gps_info.road_name')}</strong></p>
            <hr style="margin-top: 5px;">
            <p>Longitude : <strong>${this.get(dt, 'vehicle.gps_devices.0.gps_info.longitude')}</strong></p>
            <hr style="margin-top: 5px;">
            <p style="margin-bottom: 0px">Latitude : <strong>${this.get(dt, 'vehicle.gps_devices.0.gps_info.latitude')}</strong></p>
            </div>
          `;
        }

        this.popover.html(content)
          .style('left', `${event.pageX - 30}px`)
          .style('top', `${event.pageY + 20}px`);

        const tabs = this.popover.node().getElementsByClassName('nav-link');
        const tabKeys = Object.keys(tabs);
        tabKeys.map((key) => {
          const tab = tabs[key];
          tab.addEventListener('click', this.selectTab);
          return true;
        });
      };
    },
    hidePopover(e) {
      if (!isEmpty(e)) {
        const { target } = e;
        if (target.classList.contains('nav-link') ||
          target.classList.contains('nav-tabs')) {
          return false;
        }
      }

      if (!isEmpty(this.popover)) {
        this.popover = null;
        select(`.popover__${this.widgetIndex}`).remove();
      }

      return true;
    },
    selectTab(e) {
      const { target } = e;
      const popover = this.popover.node();
      const selectedId = target.getAttribute('aria-controls');
      const tabActive = popover.querySelector('.nav-link.active');
      const contentActive = popover.querySelector('.tab-pane.active');
      const targetContent = popover.querySelector(`#tab-${selectedId}`);

      const contentActiveId = contentActive.getAttribute('id');

      if (selectedId !== contentActiveId.split('-')[1]) {
        target.setAttribute('aria-selected', true);
        target.classList.add('active');

        tabActive.setAttribute('aria-selected', false);
        tabActive.classList.remove('active');

        targetContent.classList.add('show');
        targetContent.classList.add('active');

        contentActive.classList.remove('show');
        contentActive.classList.remove('active');
      }
    },
    onDropMouseover(dropType) {
      return (dt) => {
        this.tooltip = this.createPopup('tooltip', dt);
        this.tooltip
          .transition()
          .duration(200)
          .style('opacity', 1)
          .style('pointer-events', 'auto');
        let text = 'Click to see detail';

        if (dropType === 'current') {
          text = 'Click to see lastest position';
        } else if (dropType === 'origin') {
          const { code, name, metadata } = dt;

          text = `
            <div style="padding: 5px;" class="origin-info">
              <p>Code : <strong>${code}</strong></p>
              <hr style="margin-top: 5px;">
              <p>Name : <strong>${name}</strong></p>
              <hr style="margin-top: 5px;">
              <p>Address : <strong>${metadata.info.address}</strong></p>
            </div>
          `;
        }

        this.tooltip.html(text)
          .style('left', `${event.pageX - 30}px`)
          .style('top', `${event.pageY + 20}px`);
      };
    },
    onDropMouseout(dur) {
      return () => {
        const duration = dur || 5e2;
        this.tooltip
          .transition()
          .duration(duration)
          .style('opacity', 0)
          .style('pointer-events', 'none');

        setTimeout(() => {
          select(`.tooltip__${this.widgetIndex}`).remove();
        }, duration + 50);
      };
    },
    onLinkClick(linkType) {
      return (dt) => {
        const id = linkType === 'number' ? this.get(dt, 'id') : this.get(dt, 'driver.id');
        const pageTarget = linkType === 'number' ? `shipment_md/shipments/${id}` : `employees/${id}`;
        this.$router.push(pageTarget);
      };
    },
    setupHeight() {
      const dimension = this.getContainerDimension();
      const completionContainer = document.querySelector(`.completion_${this.widgetIndex}`);
      const containerHeight = dimension.height;
      completionContainer.style.height = `${containerHeight - 48}px`;
      completionContainer.style.maxHeight = `${containerHeight - 48}px`;
    },
    getContainerDimension() {
      const widgetContainer = document.querySelector(`.completion_items_${this.widgetIndex}`);
      const dim = {
        height: !isEmpty(widgetContainer) ? widgetContainer.clientHeight : 0,
        width: !isEmpty(widgetContainer) ? widgetContainer.clientWidth : 0,
      };

      return dim;
    },
    updateFakeProgress(shipment) {
      const histories = cloneDeep(this.historyProgress);
      const { destinations, number } = shipment;

      let prevHistory = null;
      let totalFinish = 0;
      const newDestinations = destinations.map((destination, i) => {
        if (isEmpty(histories[number])) {
          histories[number] = {
            key: i,
            val: 0,
          };
        }

        if ((!isEmpty(prevHistory) && prevHistory.val === 100) &&
        prevHistory.key !== destinations.length - 1) {
          histories[number] = {
            key: i,
            val: 0,
          };
        }

        const { val: lastDt, key } = histories[number];
        if (Number(key) === i && lastDt !== 100) {
          histories[number].val += 10;
          destination.percentage = histories[number].val;
        }

        if (i < Number(key) || (histories[number].val === 100 &&
          histories[number].key === destinations.length - 1)) {
          destination.percentage = 100;
          destination.finih = true;
          totalFinish += 1;
        }

        this.historyProgress = histories;
        prevHistory = histories[number];

        return destination;
      });

      shipment.destinations = newDestinations;
      if (destinations.length > 1) {
        shipment.total_percentage = prevHistory.key >= 1 && totalFinish !== 0 ?
          Math.round((totalFinish / destinations.length) * 100, 2) :
          Math.round((0.5 / destinations.length) * 100, 2);
      } else {
        shipment.total_percentage = prevHistory.val;
      }

      return shipment;
    },
    onResizeWindow() {
      window.removeEventListener('resize', this.drawChart, true);
      window.addEventListener('resize', this.drawChart, true);
    },
  },
  watch: {
    minimizeSidebar() {
      setTimeout(() => { this.drawChart(); }, 5e2);
    },
  },
  directives: {
    ClickOutside,
  },
};
</script>

<style lang="sass" scoped>
  .completion__container
    position: relative;
    overflow-y: auto;
    max-height: 500px;
    width: 100% !important;
    overflow-x: hidden;
    padding: 10px;
</style>




// WEBPACK FOOTER //
// src/components/dashboard_widgets/shipment_completion/Index.vue